import type { MergeProposalInput } from "./proposal-steps"
import { mergeProposalsForArticle } from "./proposal-steps"

// Pure decision for "should this new proposal be created, coalesced into an
// existing one, or rejected?". Lives in `packages/lib` so both the Zero mutator
// (browser + server) and the agent's Prisma persist path call the same logic.
//
// Provenance is now an *array* of issue IDs (one agent run can be triggered by
// a batch of completed issues). Same-source coalescing is a non-empty
// intersection between actor.issueIds and an existing proposal's issueIds.

export interface ExistingOpenProposalForCheck {
	id: string
	baseHtml: string
	proposedSteps: unknown[]
	authorType: "USER" | "AGENT"
	createdByUserId: string | null
	// IDs of issues the proposal's parent group is linked to. Empty for
	// MANUAL/USER proposals and for agent runs without an issue trigger.
	issueIds: string[]
}

// Shared row → check mapper. Each caller resolves `baseHtml` and the parent
// group's `issueIds` from its own data source (Zero relations vs. Prisma
// `proposalGroup.issues` include); the row→check transform is identical.
export function toExistingOpenProposal(
	row: {
		id: string
		proposedSteps: unknown
		authorType: "USER" | "AGENT"
		createdByUserId: string | null | undefined
	},
	baseHtml: string,
	issueIds: string[],
): ExistingOpenProposalForCheck {
	return {
		id: row.id,
		baseHtml,
		proposedSteps: Array.isArray(row.proposedSteps) ? row.proposedSteps : [],
		authorType: row.authorType,
		createdByUserId: row.createdByUserId ?? null,
		issueIds,
	}
}

export interface ProposalCreationActor {
	type: "USER" | "AGENT"
	// USER: must be set. AGENT: ignored for matching (use issueIds instead).
	userId?: string | null
	// AGENT: required for same-source coalescing. USER: ignored. An agent run
	// triggered by issue X coalesces with any open agent group already linked
	// to X. Multiple ids → match on non-empty intersection.
	issueIds?: string[] | null
}

export type ProposalCreationDecision =
	| { action: "create" }
	| { action: "updateExisting"; targetProposalId: string }
	| { action: "skip"; duplicateOfProposalId: string }
	| { action: "reject"; reason: string; conflictingProposalIds: string[] }

interface EvaluateArgs {
	newProposalId: string
	newBaseHtml: string
	newProposedSteps: unknown[]
	currentHtml: string
	existingOpenProposals: ExistingOpenProposalForCheck[]
	actor: ProposalCreationActor
}

function hasIntersection(a: readonly string[], b: readonly string[]): boolean {
	if (a.length === 0 || b.length === 0) return false
	const set = new Set(a)
	for (const id of b) if (set.has(id)) return true
	return false
}

function stepsEqual(a: readonly unknown[], b: readonly unknown[]): boolean {
	return JSON.stringify(a) === JSON.stringify(b)
}

// Commit/PR-triggered agent runs carry no issueIds to coalesce on and re-submit
// byte-identical steps when a push is re-delivered. Match on step equality so
// the duplicate is skipped instead of stacking another card on the article.
function findIdenticalAgentProposal(
	existing: ExistingOpenProposalForCheck[],
	newProposedSteps: readonly unknown[],
): ExistingOpenProposalForCheck | null {
	if (newProposedSteps.length === 0) return null
	return (
		existing.find(
			(p) =>
				p.authorType === "AGENT" &&
				stepsEqual(p.proposedSteps, newProposedSteps),
		) ?? null
	)
}

// Match an existing OPEN proposal that the new submission should refine in
// place. USER scope: same `createdByUserId`. AGENT scope: any shared issueId.
// Different sources never coalesce — that would mix proposals across groups.
function findCoalesceTarget(
	existing: ExistingOpenProposalForCheck[],
	actor: ProposalCreationActor,
): ExistingOpenProposalForCheck | null {
	if (actor.type === "USER") {
		if (!actor.userId) return null
		return (
			existing.find(
				(p) => p.authorType === "USER" && p.createdByUserId === actor.userId,
			) ?? null
		)
	}
	const actorIssueIds = actor.issueIds ?? []
	if (actorIssueIds.length === 0) return null
	return (
		existing.find(
			(p) =>
				p.authorType === "AGENT" && hasIntersection(actorIssueIds, p.issueIds),
		) ?? null
	)
}

export function evaluateProposalCreation(
	args: EvaluateArgs,
): ProposalCreationDecision {
	const {
		newProposalId,
		newBaseHtml,
		newProposedSteps,
		currentHtml,
		existingOpenProposals,
		actor,
	} = args

	// 0. Byte-identical agent duplicate: skip regardless of issueIds so a
	//    re-triggered commit/PR run does not create a second identical card.
	if (actor.type === "AGENT") {
		const duplicate = findIdenticalAgentProposal(
			existingOpenProposals,
			newProposedSteps,
		)
		if (duplicate) {
			return { action: "skip", duplicateOfProposalId: duplicate.id }
		}
	}

	// 1. Same-actor coalesce takes priority — refinement of an existing proposal.
	const coalesceTarget = findCoalesceTarget(existingOpenProposals, actor)
	if (coalesceTarget) {
		return { action: "updateExisting", targetProposalId: coalesceTarget.id }
	}

	// 2. No-op proposals (DELETE, manual no-change submit) bypass conflict-check.
	if (newProposedSteps.length === 0) return { action: "create" }

	// 3. Smart conflict-check: simulate accepting existing OPEN proposals + this
	//    one. If every step of the new proposal drops on the floor, accepting
	//    later would supersede with no effect — reject up front with a reason.
	const inputs: MergeProposalInput[] = existingOpenProposals.map((p) => ({
		id: p.id,
		baseHtml: p.baseHtml,
		stepsJson: p.proposedSteps,
	}))
	inputs.push({
		id: newProposalId,
		baseHtml: newBaseHtml,
		stepsJson: newProposedSteps,
	})

	const fullRun = mergeProposalsForArticle(currentHtml, inputs)
	const newOutcome = fullRun.outcomes[fullRun.outcomes.length - 1]
	if (!newOutcome) return { action: "create" }

	// `applied: true` means zero conflicts on this proposal's steps.
	if (newOutcome.applied) return { action: "create" }

	// Anything less than full conflict means at least one step landed; matches
	// the accept-time invariant of "supersede only when there is no net change."
	if (newOutcome.conflicts.length < newProposedSteps.length) {
		return { action: "create" }
	}

	return {
		action: "reject",
		reason: formatRejectionReason({
			newProposedStepsLength: newProposedSteps.length,
			conflictsLength: newOutcome.conflicts.length,
			blockingProposals: existingOpenProposals,
		}),
		conflictingProposalIds: existingOpenProposals.map((p) => p.id),
	}
}

function formatRejectionReason(args: {
	newProposedStepsLength: number
	conflictsLength: number
	blockingProposals: ExistingOpenProposalForCheck[]
}): string {
	const { newProposedStepsLength, conflictsLength, blockingProposals } = args
	const noun = newProposedStepsLength === 1 ? "change" : "changes"
	const blockerCount = blockingProposals.length
	const blockerNoun = blockerCount === 1 ? "proposal" : "proposals"
	if (blockerCount === 0) {
		// No existing OPEN proposals but the rebase still wiped every step —
		// the article has been edited in a way that fully obsoletes the submission.
		return `${conflictsLength} of ${newProposedStepsLength} ${noun} no longer apply — the article has changed since the proposal was drafted`
	}
	return `${conflictsLength} of ${newProposedStepsLength} ${noun} conflict with ${blockerCount} other open ${blockerNoun} on this article. Resolve those first to retry.`
}
